import React from 'react';
import { Box, Text, Flex, Input, IconButton, HStack, SimpleGrid, Checkbox, FormControl, FormLabel, FormErrorMessage } from '@chakra-ui/react';
import { FaEdit, FaTimes, FaCheck, FaExclamationTriangle } from 'react-icons/fa';
import UserAvatar from './UserAvatar';
import { formatPhoneNumber } from '../utils/stringUtils';

// Simple validators
const isValidText = (text: string) => /[a-zA-Z0-9]{2,}/.test(text);

const fields = [
  { key: 'username', label: 'Username / Email', type: 'email', validator: isValidText },
  { key: 'email', label: 'Email', type: 'email', validator: (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) },
  { key: 'first_name', label: 'First Name', type: 'text', validator: isValidText },
  { key: 'last_name', label: 'Last Name', type: 'text', validator: isValidText },
  { key: 'phone', label: 'Phone', type: 'tel', validator: (phone: string) => /^\+?\d{10,15}$/.test(phone.replace(/[^\d]/g, '')) },
  { key: 'instagram', label: 'Instagram', type: 'text', validator: isValidText },
  { key: 'x', label: 'X', type: 'text', validator: isValidText },
  { key: 'facebook', label: 'Facebook Page', type: 'text', validator: isValidText },
];

interface ProfileTabProps {
  user: any;
  editing: { [key: string]: boolean };
  setEditing: (editing: { [key: string]: boolean }) => void;
  editValues: { [key: string]: string };
  setEditValues: (editValues: { [key: string]: string }) => void;
  isSaving: { [key: string]: boolean };
  setIsSaving: (isSaving: { [key: string]: boolean }) => void;
  showName: boolean;
  setShowName: (showName: boolean) => void;
  username: string;
  setUsername: (username: string) => void;
  usernameTouched: boolean;
  setUsernameTouched: (touched: boolean) => void;
  handleEdit: (key: string) => void;
  handleCancel: (key: string) => void;
  handleChange: (key: string, value: string) => void;
  handleSave: (key: string) => void;
}

const ProfileTab: React.FC<ProfileTabProps> = ({
  user,
  editing,
  setEditing,
  editValues,
  setEditValues,
  isSaving,
  setIsSaving,
  showName,
  setShowName,
  username,
  setUsername,
  usernameTouched,
  setUsernameTouched,
  handleEdit,
  handleCancel,
  handleChange,
  handleSave
}) => {
  // Username validation
  const usernameError = !showName && usernameTouched && !username.trim();

  return (
    <Box maxW="700px" mx="auto" mt={4}>
      {/* Show Name Checkbox and Username Field */}
      <Flex mb={6} gap={6} align="center">
        <Checkbox
          isChecked={showName}
          onChange={e => setShowName(e.target.checked)}
          colorScheme="blue"
        >
          Show my real name to all other users
        </Checkbox>
        {!showName && (
          <FormControl isRequired isInvalid={!!usernameError} maxW="300px">
            <FormLabel mb={1}>Username</FormLabel>
            <Input
              value={username}
              onChange={e => setUsername(e.target.value)}
              onBlur={() => setUsernameTouched(true)}
              autoComplete="nickname"
            />
            {usernameError && <FormErrorMessage>Username is required.</FormErrorMessage>}
          </FormControl>
        )}
      </Flex>
      {/* Warning for missing first/last name */}
      {(!isValidText(user.first_name) || !isValidText(user.last_name)) && (
        <Flex mb={6} align="center" bg="yellow.100" color="yellow.800" borderRadius="md" p={3} maxW="540px" mx="auto">
          <Box mr={3} display="flex" alignItems="center">
            <FaExclamationTriangle style={{ fontSize: '1.5em' }} />
          </Box>
          <Text fontWeight="semibold">We highly recommend you provide your first and last names</Text>
        </Flex>
      )}
      <Flex>
        <Box flexShrink={0} minW="80px" maxW="80px" display="flex" alignItems="flex-start" pt={2} mr={6}>
          <UserAvatar user={user} size="lg" />
        </Box>
        <Box flex="1">
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={6}>
            {fields.map(({ key, label, type, validator }) => {
              const value = String(user[key as keyof typeof user] ?? '');
              const isEditing = !!editing[key];
              const editValue = String(editValues[key] ?? value);
              const isValid = validator(editValue);
              
              // Format phone number for display
              const displayValue = key === 'phone' ? formatPhoneNumber(value) : value;
              
              return (
                <Flex key={key} align="center" mb={2} w="100%">
                  <Box w="100%" maxW="260px">
                    <Text fontWeight="bold" mb={1} fontSize="md">{label}</Text>
                    {isEditing ? (
                      <HStack>
                        <Input
                          type={type}
                          value={editValue}
                          onChange={e => handleChange(key, e.target.value)}
                          size="sm"
                          fontSize="md"
                          bg="whiteAlpha.100"
                          color="white"
                          borderColor={isValid ? 'green.400' : 'red.400'}
                          _hover={{ borderColor: isValid ? 'green.500' : 'red.500' }}
                        />
                        <IconButton
                          aria-label="Cancel"
                          size="sm"
                          bg="whiteAlpha.100"
                          color="white"
                          _hover={{ bg: 'whiteAlpha.200' }}
                          variant="ghost"
                          onClick={() => handleCancel(key)}
                        >
                          <FaTimes />
                        </IconButton>
                        <IconButton
                          aria-label="Save"
                          size="sm"
                          bg="whiteAlpha.100"
                          color="white"
                          _hover={{ bg: 'whiteAlpha.200' }}
                          variant="ghost"
                          onClick={() => isValid && handleSave(key)}
                          isLoading={isSaving[key]}
                        >
                          <FaCheck color={isValid ? '#22c55e' : '#a0aec0'} />
                        </IconButton>
                      </HStack>
                    ) : (
                      <HStack>
                        <Text w="full" color={value ? 'inherit' : 'gray.400'} fontStyle={value ? 'normal' : 'italic'} fontSize="md">
                          {displayValue || 'Not provided'}
                        </Text>
                        <IconButton
                          aria-label="Edit"
                          size="sm"
                          bg="whiteAlpha.100"
                          color="white"
                          _hover={{ bg: 'whiteAlpha.200' }}
                          variant="ghost"
                          onClick={() => handleEdit(key)}
                        >
                          <FaEdit />
                        </IconButton>
                      </HStack>
                    )}
                  </Box>
                </Flex>
              );
            })}
          </SimpleGrid>
        </Box>
      </Flex>
    </Box>
  );
};

export default ProfileTab;
